/**
 * Klarna Payment Provider
 * Handles Klarna checkout integration with customer form approach (like Bambora)
 */

import { PaymentProvider } from './base.js';
import { getCurrencyCode } from '../../core/i18n.js';

export class KlarnaProvider extends PaymentProvider {
    constructor(config, system) {
        super(config, system);
        this.providerType = 'klarna';
    }

    async processOrder(order) {
        // Note: This method is now deprecated for Klarna
        // Klarna checkout should use the customer form approach in Checkout_Template.html
        // This is kept for backward compatibility but will redirect to form-based checkout
        
        console.warn('KlarnaProvider.processOrder is deprecated. Use customer form approach instead.');
        
        // Redirect to checkout page with customer form
        const checkoutUrl = this.config.checkoutUrl || '/checkout/';
        window.location.href = checkoutUrl;
        
        return {
            success: true,
            message: 'Redirecting to customer form checkout',
            checkoutUrl: checkoutUrl
        };
    }

    async confirmOrder(orderId) {
        const url = `${this.config.confirmApiUrl}?merchantId=${this.config.merchantId}&orderId=${orderId}&provider=klarna`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept-Language': this.config.locale,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Klarna confirmation error! status: ${response.status}`);
        }

        return await response.json();
    }

    renderCheckout(data) {
        if (data.htmlSnippet) {
            const checkoutContainer = document.getElementById('klarna');
            if (checkoutContainer) {
                checkoutContainer.innerHTML = data.htmlSnippet;
                this.executeScripts(checkoutContainer);
            }
        }
    }

    /**
     * Helper method for the new API approach (used by customer form)
     * @param {Object} orderData - Order data from customer form
     * @returns {Promise<Object>} API response
     */
    static async initiatePayment(orderData) {
        const response = await fetch('/cart/api/v1/payments/initiate/klarna', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Klarna API error! status: ${response.status} - ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Build order data for Klarna API
     * @param {Object} customerFormData - Customer form data
     * @param {Array} cartItems - Cart items
     * @param {Object} config - Configuration
     * @returns {Object} Order data for API
     */
    static buildOrderData(customerFormData, cartItems, config) {
        // NOTE: Total amount is NOT calculated in JavaScript
        // Backend calculates the correct total from OrderRows, handling B2C/B2B VAT correctly
        
        // Build customer object according to CustomerModel structure
        const customer = {
            name: customerFormData.name || '',
            lastname: customerFormData.lastname || '',
            companyName: customerFormData.companyName || null,
            address: customerFormData.address || '',
            zip: customerFormData.zip || '',
            city: customerFormData.city || '',
            country: customerFormData.country || 'SE',
            email: customerFormData.email || '',
            phone: customerFormData.phone || '',
            type: customerFormData.companyName ? 1 : 0, // 1 = Company, 0 = Private
            organizationRegistrationId: customerFormData.organizationRegistrationId || null,
            vatId: null
        };

        // Build order rows according to OrderRowModel structure
        const orderRows = cartItems.map(item => {
            // Identity (GUIDs are translation-proof), quantity, and for gift cards the chosen
            // amount via giftCardData. Type and TaxRate are intentionally NOT sent - the server
            // derives them from the database.
            const orderRow = {
                productNr: item.ProductNr,
                productName: item.ProductName,
                productPrice: parseFloat(item.ProductPrice),
                orderCount: item.OrderCount,
                orderUnit: 'st',
                rowSum: parseFloat(item.ProductPrice) * item.OrderCount,
                productUrl: item.ProductUrl || '',
                imageUrl: item.ImageUrl || ''
            };

            // Forward the translation-proof identifiers when present
            if (item.ProductId) {
                orderRow.productId = item.ProductId;
            }
            if (item.ProductVariantId) {
                orderRow.productVariantId = item.ProductVariantId;
            }
            
            // CRITICAL: Add GiftCardData if this is a gift card
            if (item.isGiftCard && item.giftCardData) {
                orderRow.giftCardData = item.giftCardData;
                console.log('Added GiftCardData to order row for product:', item.ProductNr, 'Data:', item.giftCardData);
            }
            
            return orderRow;
        });

        // Build delivery address if different delivery address is selected
        let deliveryAddress = null;
        if (customerFormData.differentDeliveryAddress) {
            deliveryAddress = {
                name: customerFormData.deliveryName || '',
                lastname: customerFormData.deliveryLastname || '',
                companyName: customerFormData.deliveryCompany || null,
                address: customerFormData.deliveryAddress || '',
                zip: customerFormData.deliveryZip || '',
                city: customerFormData.deliveryCity || '',
                country: 'SE',
                email: customerFormData.deliveryEmail || customer.email,
                phone: customerFormData.deliveryPhone || customer.phone
            };
        }

        // Get discount data from DiscountManager if available
        let discounts = [];
        if (window.ecommerce && window.ecommerce.checkout && window.ecommerce.checkout.discountManager) {
            const discountManager = window.ecommerce.checkout.discountManager;
            if (discountManager.discountStack && discountManager.discountStack.length > 0) {
                discounts = discountManager.discountStack.map(discount => ({
                    code: discount.code,
                    amount: Math.round(discount.discountAmount * 100), // Convert to öre
                    description: discount.description || discount.name || `Rabatt: ${discount.code}`
                }));
                console.log('KlarnaProvider: Including multiple discounts in order:', discounts);
            }
        }

        // Get gift card payments from the GiftCardPaymentManager (Phase 3, max 1 card, B6).
        // Note: Klarna merchants are blocked server-side and the section is
        // never shown for them, so this is always an empty array for Klarna.
        const giftCardPayments =
            window.ecommerce?.checkout?.giftCardPaymentManager?.getGiftCardPayments?.() || [];

        // Get currency and locale from i18n system or config
        const currency = getCurrencyCode() || config.purchaseCurrency || 'SEK';
        const locale = config.locale || 'sv-SE';
        const purchaseCountry = config.purchaseCountry || 'se';

        // Create order data according to InitiatePaymentRequest structure
        // NOTE: No 'amount' field - backend calculates from OrderRows
        return {
            merchantId: config.merchantId || '00000000-0000-0000-0000-000000000001',
            currency: currency,
            orderReference: `${Date.now()}`,
            returnUrl: this.absoluteUrl(config.confirmUrl, '/thank-you/'),
            cancelUrl: this.absoluteUrl(config.checkoutUrl, '/checkout/'),
            locale: locale,
            purchaseCountry: purchaseCountry,
            customer: customer,
            orderRows: orderRows,
            deliveryAddress: deliveryAddress,
            sendReceiptToDeliveryAddress: customerFormData.sendReceiptToDeliveryAddress === 'on',
            termsUrl: config.termsUrl ? this.absoluteUrl(config.termsUrl) : null,
            discounts: discounts,
            giftCardPayments: giftCardPayments,
            ShippingData: customerFormData.shippingData
        };
    }
}
